import type { PropSchema } from "@/stores/templatesStore";

/**
 * Resolved chain scope for a token-picker template prop.
 *
 * Exactly one of `chainId` / `chainIds` will be set — or both undefined
 * if the template has not pinned any chain.
 *
 * `showChainFilter` tells the picker whether to render the chain-filter
 * pill row. We hide it in the single-chain case (no meaningful choice
 * left) and show it in the multi-chain and unpinned cases.
 */
export interface ChainScope {
  chainId: number | undefined;
  chainIds: number[] | undefined;
  showChainFilter: boolean;
}

/**
 * Resolves a template prop's chain scope from any mix of four schema keys:
 *
 * Dynamic (sibling prop lookup):
 *   - `chainIdsPropKey` → expects `propValues[key]` to be `number[]`
 *   - `chainPropKey`    → expects `propValues[key]` to be a single number
 *
 * Static (literal in the template JSON):
 *   - `chainIds` → number[]
 *   - `chainId`  → number
 *
 * Precedence (first non-empty wins):
 *   chainIdsPropKey > chainIds > chainPropKey > chainId
 *
 * Plural forms take precedence over singular forms at the same layer.
 * Dynamic lookups take precedence over static literals.
 *
 * Every candidate is coerced via `Number(...)` (on numbers and numeric
 * strings only) and filtered with `Number.isInteger(n) && n > 0` —
 * NaN, null, negatives, zero, floats (1.5), booleans, objects, and
 * non-numeric strings are silently dropped. Duplicates are removed.
 *
 * Behavior:
 *   - No valid chains → unpinned; picker shows all chains.
 *   - Exactly one → single-chain lock; pill row hidden.
 *   - Multiple    → restricted to that set; pill row shown (users
 *                   can still narrow within the allowed set).
 */
export function resolveChainScope(
  schema: PropSchema | undefined,
  propValues: Record<string, unknown> | undefined,
): ChainScope {
  const collect = (raw: unknown): number[] => {
    const arr = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    const out: number[] = [];
    const seen = new Set<number>();
    for (const item of arr) {
      // Accept numbers and numeric strings only. Booleans coerce via Number()
      // (true→1, false→0) and would otherwise slip through as valid chain IDs.
      const n = typeof item === "number" ? item : typeof item === "string" ? Number(item) : Number.NaN;
      // Chain IDs are positive integers — reject floats, zero, negatives, NaN.
      if (Number.isInteger(n) && n > 0 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  };

  const candidates: Array<unknown> = [];

  if (schema?.chainIdsPropKey && propValues) candidates.push(propValues[schema.chainIdsPropKey]);
  if (schema?.chainIds !== undefined) candidates.push(schema.chainIds);
  if (schema?.chainPropKey && propValues) candidates.push(propValues[schema.chainPropKey]);
  if (schema?.chainId !== undefined) candidates.push(schema.chainId);

  let resolved: number[] = [];
  for (const c of candidates) {
    const nums = collect(c);
    if (nums.length > 0) {
      resolved = nums;
      break;
    }
  }

  if (resolved.length === 0) {
    return { chainId: undefined, chainIds: undefined, showChainFilter: true };
  }
  if (resolved.length === 1) {
    return { chainId: resolved[0], chainIds: undefined, showChainFilter: false };
  }
  return { chainId: undefined, chainIds: resolved, showChainFilter: true };
}
